import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Ball, NumberBoard } from '../../bingo/ui.js';
import { EndGameDialog, useCloseConfirm, Winners } from './BingoAdmin.js';
import { NearCoupons } from './NearCoupons.js';
import { BINGO_SOUND_MS } from '../../../shared/bingoSounds.js';
import {
  BINGO_AUTO_INTERVAL_MAX,
  BINGO_AUTO_INTERVAL_MIN,
  DEFAULT_AUTO_CONFIG,
  stageLabel,
  type BingoAdView,
  type BingoAutoConfig,
  type BingoBatchView,
  type BingoHostState,
} from '../../../shared/bingo.js';

// Automatic bingo for the administrator (decisions 2026-09-30): the running game (pause, end,
// Bingo! presses), the settings and the schedule, and the sponsor ad library.

type Flash = { ok: boolean; text: string } | null;
const a = () => t.admin.autoBingo;
const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];

interface AutoView {
  config: BingoAutoConfig;
  plan: { introAt: number; closeAt: number } | null;
  ads: BingoAdView[];
  running: { paused: boolean } | null;
  /** An audio stream address has been set (Äänitiedostot, admin). */
  stream: boolean;
}

const clock = (ms: number) => new Date(ms).toLocaleString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
/** "datetime-local" value in local time. */
const toLocalInput = (ms: number) => {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const seconds = (ms: number) => `${Math.round(ms / 100) / 10} s`;

// --- Running game -----------------------------------------------------------------------------

export function AutoPanel(props: { host: BingoHostState; onHost: (h: BingoHostState) => void; onFlash: (f: Flash) => void }) {
  const game = props.host.game!;
  const auto = props.host.auto;
  const [busy, setBusy] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, []);
  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    try {
      props.onHost(await api<BingoHostState>('POST', path, body));
      props.onFlash(null);
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const last = game.draws[game.draws.length - 1];
  const offset = props.host.now - Date.now();
  return (
    <section class="card bingo-host">
      <div class="bingo-host-head">
        <div>
          <h2>
            {a().runningTitle}: {props.host.labels.join(' + ')}
          </h2>
          <div class="bingo-host-stage">{t.admin.bingo.stage(stageLabel(game.stage))}</div>
          <div class="muted">{t.admin.bingo.drawn(game.draws.length, game.players)}</div>
          {auto?.paused && <p class="badge">{a().pausedBadge}</p>}
          {auto?.jackpot && <p class="muted">{a().jackpotState(auto.jackpot.state, auto.jackpot.draws)}</p>}
        </div>
        <div class="bingo-host-last">{last !== undefined ? <Ball n={last} size={120} key={last} /> : <span class="muted">{a().starting}</span>}</div>
      </div>
      {auto?.claims && (
        <div class="bingo-panel has-bingo" role="status">
          <h3>{a().claims(Math.max(0, Math.ceil((auto.claims.endsAt - now - offset) / 1000)))}</h3>
          {auto.claims.cards.map((c) => (
            <div key={c.cardNumber}>{t.admin.bingo.cardLabel(c.cardNumber, c.nickname)}</div>
          ))}
        </div>
      )}
      <div class="bingo-start-actions">
        {auto && (
          <button class="btn btn-primary" disabled={busy} onClick={() => void act('/api/admin/bingo/auto/pause', { paused: !auto.paused })}>
            {auto.paused ? `▶ ${a().resume}` : `⏸ ${a().pause}`}
          </button>
        )}
        <button class="btn btn-danger" disabled={busy} onClick={() => setConfirmEnd(true)}>
          {t.admin.bingo.end}
        </button>
      </div>
      <p class="hint">{a().runningInfo}</p>
      <NearCoupons host={props.host} />
      <NumberBoard draws={game.draws} class="bingo-host-board" />
      <Winners winners={game.winners} />
      {confirmEnd && (
        <EndGameDialog
          onEnd={() => {
            setConfirmEnd(false);
            void act('/api/admin/bingo/auto/stop');
          }}
          onCancel={() => setConfirmEnd(false)}
        />
      )}
    </section>
  );
}

// --- Settings and schedule --------------------------------------------------------------------

export function AutoSettings(props: { batches: BingoBatchView[]; running: 'host' | 'auto' | null; onStarted: (h: BingoHostState) => void; onFlash: (f: Flash) => void }) {
  const [view, setView] = useState<AutoView | null>(null);
  const [cfg, setCfg] = useState<BingoAutoConfig>(DEFAULT_AUTO_CONFIG);
  const [when, setWhen] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const apply = (v: AutoView) => {
    setView(v);
    setCfg(v.config);
    setWhen(v.config.schedule ? toLocalInput(v.config.schedule.at) : '');
    if (v.config.schedule) setSelected(new Set(v.config.schedule.batchIds));
  };
  useEffect(() => {
    api<AutoView>('GET', '/api/admin/bingo/auto')
      .then(apply)
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  }, []);

  const set = <K extends keyof BingoAutoConfig>(key: K, value: BingoAutoConfig[K]) => setCfg({ ...cfg, [key]: value });
  const num = (key: 'intervalS' | 'closeBeforeMin' | 'minCoupons' | 'maxCoupons' | 'fillerEvery') => ({
    value: cfg[key],
    onInput: (e: Event) => set(key, Number((e.currentTarget as HTMLInputElement).value)),
  });
  const toggleBatch = (id: number, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
  };

  const save = async (schedule: BingoAutoConfig['schedule']) => {
    setBusy(true);
    try {
      apply(await api<AutoView>('PUT', '/api/admin/bingo/auto', { ...cfg, schedule }));
      props.onFlash({ ok: true, text: schedule ? a().scheduled(clock(schedule.at)) : a().saved });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: Event) => {
    e.preventDefault();
    if (!when) return void save(null);
    const at = new Date(when).getTime();
    if (Number.isNaN(at) || at < Date.now() + 60_000) return props.onFlash({ ok: false, text: a().whenPast });
    if (selected.size === 0) return props.onFlash({ ok: false, text: a().chooseBatch });
    void save({ at, batchIds: [...selected] });
  };
  const closing = useCloseConfirm(props.onFlash);
  const startNow = async () => {
    setBusy(true);
    try {
      props.onStarted(await api<BingoHostState>('POST', '/api/admin/bingo/auto/start', { batchIds: [...selected] }));
      props.onFlash({ ok: true, text: a().startedNow });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  if (!view) return <p class="muted">{a().loading}</p>;
  const players = props.batches.filter((x) => selected.has(x.id)).reduce((sum, x) => sum + x.claimed, 0);
  const adList = view.ads.filter((x) => x.kind === 'ad');
  const fillerCount = view.ads.length - adList.length;
  // Why "start now" cannot be used (the button is not greyed out without a reason).
  const blocked =
    props.running === 'host'
      ? a().blockedHost
      : props.running === 'auto'
        ? a().blockedAuto
        : selected.size === 0
          ? a().blockedChoose
          : players === 0
            ? a().blockedNoCoupons
            : null;
  return (
    <form class="card form" onSubmit={submit}>
      <h2>{a().title}</h2>
      <p class="hint">{a().info}</p>

      <div class="grid-2">
        <label>
          {a().interval}
          <input type="number" min={BINGO_AUTO_INTERVAL_MIN} max={BINGO_AUTO_INTERVAL_MAX} required {...num('intervalS')} />
        </label>
        <label>
          {a().closeBefore}
          <input type="number" min={0} max={1440} required {...num('closeBeforeMin')} />
        </label>
        <label>
          {a().minCoupons}
          <input type="number" min={1} max={10000} required {...num('minCoupons')} />
        </label>
        <label>
          {a().maxCoupons}
          <input type="number" min={1} max={10000} required {...num('maxCoupons')} />
        </label>
      </div>

      <fieldset class="card-format">
        <legend>{a().announce}</legend>
        <label class="radio">
          <input type="radio" name="auto-announce" checked={cfg.announce === 'auto'} onChange={() => set('announce', 'auto')} />
          {a().announceAuto}
        </label>
        <label class="radio">
          <input type="radio" name="auto-announce" checked={cfg.announce === 'manual'} onChange={() => set('announce', 'manual')} />
          {a().announceManual}
        </label>
      </fieldset>

      <label class="switch">
        <input type="checkbox" checked={cfg.music} onChange={(e) => set('music', e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {a().music}
      </label>
      <label class="switch">
        <input type="checkbox" checked={cfg.background} onChange={(e) => set('background', e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {a().background}
      </label>
      {/* The waiting music (user request 2026-10-03): the tracks of Äänitiedostot, or the stream. */}
      <label class="switch">
        <input type="checkbox" checked={cfg.waitMusic} onChange={(e) => set('waitMusic', e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {a().waitMusic}
      </label>
      {cfg.waitMusic && (
        <fieldset class="send-mode">
          <legend>{a().waitSource}</legend>
          <label>
            <input type="radio" checked={!cfg.waitMusicStream && cfg.waitMusicOrder === 'list'} onChange={() => setCfg({ ...cfg, waitMusicStream: false, waitMusicOrder: 'list' })} />{' '}
            {a().orderList}
          </label>
          <label>
            <input type="radio" checked={!cfg.waitMusicStream && cfg.waitMusicOrder === 'random'} onChange={() => setCfg({ ...cfg, waitMusicStream: false, waitMusicOrder: 'random' })} />{' '}
            {a().orderRandom}
          </label>
          {view?.stream && (
            <label>
              <input type="radio" checked={cfg.waitMusicStream} onChange={() => set('waitMusicStream', true)} /> {a().sourceStream}
            </label>
          )}
        </fieldset>
      )}
      {/* The rules read out after the claiming has closed (user request 2026-10-03). */}
      <label class="switch">
        <input type="checkbox" checked={cfg.instructions} onChange={(e) => set('instructions', e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {a().instructions}
      </label>
      {cfg.instructions && !BINGO_SOUND_MS['ohjeet'] && <p class="muted">{a().instructionsMissing}</p>}

      <label class="switch">
        <input type="checkbox" checked={cfg.jackpot} onChange={(e) => set('jackpot', e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {a().jackpot}
      </label>

      <fieldset class="card-format">
        <legend>{a().adsTitle}</legend>
        <label class="radio">
          <input type="radio" name="auto-ads" checked={cfg.ads} onChange={() => set('ads', true)} />
          {a().adsOn}
        </label>
        <label class="radio">
          <input type="radio" name="auto-ads" checked={!cfg.ads} onChange={() => set('ads', false)} />
          {a().adsOff}
        </label>
        <p class="hint">{a().carouselSummary(adList.filter((x) => cfg.adIds.includes(x.id)).length, adList.length)}</p>
      </fieldset>

      <label class="switch">
        <input type="checkbox" checked={cfg.fillers} onChange={(e) => set('fillers', e.currentTarget.checked)} />
        <span class="switch-track" aria-hidden="true" />
        {a().fillers(fillerCount)}
      </label>
      {cfg.fillers && (
        <label>
          {a().fillerEvery}
          <input type="number" min={2} max={75} required {...num('fillerEvery')} />
        </label>
      )}

      <fieldset class="card-format">
        <legend>{a().batches}</legend>
        {props.batches.length === 0 ? (
          <p class="muted">{t.admin.bingo.none}</p>
        ) : (
          props.batches.map((x) => (
            <label class="switch" key={x.id}>
              <input type="checkbox" checked={selected.has(x.id)} onChange={(e) => toggleBatch(x.id, e.currentTarget.checked)} />
              <span class="switch-track" aria-hidden="true" />
              <span>
                <b>{x.label}</b> · {t.admin.bingo.claimed(x.claimed, x.size)} ·{' '}
                {x.registrationOpen ? t.admin.bingo.registrationOpen : t.admin.bingo.registrationClosed}
              </span>
            </label>
          ))
        )}
        {selected.size > 0 && <p>{a().selectedPlayers(players)}</p>}
      </fieldset>

      <label>
        {a().when}
        <input type="datetime-local" value={when} onInput={(e) => setWhen(e.currentTarget.value)} />
      </label>
      {view.config.schedule && view.plan ? (
        <p class="success">{a().plan(clock(view.config.schedule.at), clock(view.plan.introAt), clock(view.plan.closeAt))}</p>
      ) : (
        <p class="hint">{a().whenInfo}</p>
      )}

      <div class="bingo-start-actions">
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {when ? a().saveSchedule : a().save}
        </button>
        {view.config.schedule && (
          <button class="btn btn-danger" type="button" disabled={busy} onClick={() => void save(null)}>
            {a().removeSchedule}
          </button>
        )}
        <button class="btn btn-primary" type="button" disabled={busy || blocked !== null} onClick={() => closing.ask(props.batches.filter((x) => selected.has(x.id) && x.registrationOpen).map((x) => x.id), () => void startNow())}>
          ▶ {a().startNow}
        </button>
      </div>
      {blocked ? <p class="error">{blocked}</p> : <p class="hint">{a().startNowInfo}</p>}
      {closing.dialog}
    </form>
  );
}

// --- Sponsor ads ------------------------------------------------------------------------------

/** "data:…;base64,XXXX" → "XXXX" */
const readBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Target levels (RMS of the non-silent 400 ms blocks): ads that of the start music, fillers that of
 * the speech (≈ −29).
 */
const LEVEL_DB = { ad: -27, filler: -29 } as const;

/** Length and a volume that levels the ad to the speech (the browser measures; no tools on the server). */
export async function measure(file: File, target: number): Promise<{ ms: number; volume: number }> {
  const ctx = new AudioContext();
  try {
    const buffer = await ctx.decodeAudioData(await file.arrayBuffer());
    const block = Math.floor(buffer.sampleRate * 0.4);
    const levels: number[] = [];
    for (let start = 0; start + block <= buffer.length; start += block) {
      let sum = 0;
      for (let c = 0; c < buffer.numberOfChannels; c++) {
        const data = buffer.getChannelData(c);
        for (let i = start; i < start + block; i++) sum += data[i]! * data[i]!;
      }
      const db = 10 * Math.log10(sum / (block * buffer.numberOfChannels) + 1e-12);
      if (db > -50) levels.push(db);
    }
    const level = levels.length ? 10 * Math.log10(levels.reduce((s, db) => s + 10 ** (db / 10), 0) / levels.length) : target;
    return { ms: Math.round(buffer.duration * 1000), volume: Math.min(1, Math.max(0.05, 10 ** ((target - level) / 20))) };
  } finally {
    void ctx.close();
  }
}

/** The library of sponsor ads, or of fillers (phrases, jokes; 2026-10-01) – the same, without images. */
export function AdsLibrary(props: { kind: 'ad' | 'filler'; onFlash: (f: Flash) => void; /** Only the upload form (the carousel lists the ads itself). */ uploadOnly?: boolean; onChanged?: () => void }) {
  const L = props.kind === 'ad' ? a().adTexts : a().fillerTexts;
  const mine = (list: BingoAdView[]) => list.filter((x) => x.kind === props.kind);
  const [ads, setAds] = useState<BingoAdView[] | null>(null);
  const [name, setName] = useState('');
  const [audio, setAudio] = useState<File | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<BingoAdView | null>(null);
  const [formKey, setFormKey] = useState(0);

  const load = () =>
    api<AutoView>('GET', '/api/admin/bingo/auto')
      .then((v) => setAds(mine(v.ads)))
      .catch((err) => props.onFlash({ ok: false, text: errorText(err) }));
  useEffect(() => {
    void load();
  }, []);

  const upload = async (e: Event) => {
    e.preventDefault();
    if (!audio) return;
    if (audio.size > 6 * 1024 * 1024) return props.onFlash({ ok: false, text: a().audioTooBig });
    if (image && image.size > 1024 * 1024) return props.onFlash({ ok: false, text: a().imageTooBig });
    setBusy(true);
    try {
      const measured = await measure(audio, LEVEL_DB[props.kind]).catch(() => null);
      if (!measured) return props.onFlash({ ok: false, text: a().audioBad });
      await api('POST', '/api/admin/bingo/ads', {
        kind: props.kind,
        name: name.trim(),
        audio: await readBase64(audio),
        ms: measured.ms,
        volume: measured.volume,
        image: image && props.kind === 'ad' ? { mime: image.type, data: await readBase64(image) } : null,
      });
      props.onFlash({ ok: true, text: L.saved(name.trim()) });
      setName('');
      setAudio(null);
      setImage(null);
      setFormKey(formKey + 1);
      await load();
      props.onChanged?.();
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  };
  const remove = async (ad: BingoAdView) => {
    setRemoving(null);
    try {
      setAds(mine((await api<AutoView>('DELETE', `/api/admin/bingo/ads/${ad.id}`)).ads));
      props.onFlash({ ok: true, text: L.removed(ad.name) });
    } catch (err) {
      props.onFlash({ ok: false, text: errorText(err) });
    }
  };

  return (
    <>
      <form class="card form" onSubmit={upload} key={formKey}>
        <h2>{L.new}</h2>
        <p class="hint">{L.newInfo}</p>
        <label>
          {L.name}
          <input value={name} maxLength={60} onInput={(e) => setName(e.currentTarget.value)} required />
        </label>
        <label>
          {a().adAudio}
          <input type="file" accept="audio/mpeg,.mp3" onChange={(e) => setAudio(e.currentTarget.files?.[0] ?? null)} required />
        </label>
        {props.kind === 'ad' && (
          <label>
            {a().adImage}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setImage(e.currentTarget.files?.[0] ?? null)} />
          </label>
        )}
        <button class="btn btn-primary" type="submit" disabled={busy || !audio}>
          {busy ? a().uploading : a().upload}
        </button>
      </form>
      {!props.uploadOnly && (
      <section class="card">
        <h2>{L.title}</h2>
        {!ads ? (
          <p class="muted">{a().loading}</p>
        ) : ads.length === 0 ? (
          <p class="muted">{L.empty}</p>
        ) : (
          <ul class="user-list">
            {ads.map((ad) => (
              <li class="user-row bingo-ad-row" key={ad.id}>
                {ad.hasImage && <img class="bingo-ad-thumb" src={`/api/bingo/ads/${ad.id}/image`} alt="" />}
                <div class="user-main">
                  <div class="user-name">{ad.name}</div>
                  <div class="user-sub">{a().adMeta(seconds(ad.ms), Math.round(ad.volume * 100), ad.hasImage)}</div>
                  <audio
                    controls
                    preload="none"
                    src={`/api/bingo/ads/${ad.id}/audio`}
                    ref={(el) => {
                      if (el) el.volume = ad.volume;
                    }}
                  />
                </div>
                <div class="user-actions">
                  <button class="btn btn-small btn-danger" onClick={() => setRemoving(ad)}>
                    {t.admin.bingo.remove}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      )}
      {removing && (
        <ConfirmDialog
          title={a().removeTitle(removing.name)}
          confirmLabel={t.admin.bingo.remove}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>{a().removeText}</p>
        </ConfirmDialog>
      )}
    </>
  );
}
